import { useParams, Link } from 'react-router-dom';
import SEOMeta from '@/components/feature/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16">
          <div className="container-custom py-20 text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-6">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl text-foreground-900 mb-3">文章未找到</h1>
            <p className="text-foreground-600 mb-6">您要查看的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-lg font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.publishedAt,
    url: `https://aiqingdao-navigation.com/article/${article.slug}`,
    author: {
      '@type': 'Organization',
      name: '爱情岛论坛导航',
    },
    publisher: {
      '@type': 'Organization',
      name: '爱情岛论坛导航',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://aiqingdao-navigation.com/' },
      { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://aiqingdao-navigation.com/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={`${article.title} - 爱情岛论坛导航`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://aiqingdao-navigation.com/article/${article.slug}`}
        ogType="article"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="container-custom">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: article.categoryName, path: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="container-custom py-10">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-5">
              <Link
                to={`/category/${article.category}`}
                className="text-xs px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 font-medium hover:bg-primary-200 transition-colors whitespace-nowrap no-underline"
              >
                {article.categoryName}
              </Link>
              <span className="text-sm text-foreground-500 whitespace-nowrap">{article.publishedAt}</span>
              <span className="text-sm text-foreground-500 whitespace-nowrap">{article.readTime}阅读</span>
            </div>

            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-900 mb-6 leading-snug">
              {article.title}
            </h1>

            <div className="flex items-center gap-3 mb-8 flex-wrap">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="aspect-[16/9] rounded-lg overflow-hidden mb-10">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover"
                title={`${article.title} - 爱情岛论坛导航`}
              />
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex items-center justify-between">
                <Link
                  to={`/category/${article.category}`}
                  className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回{article.categoryName}
                </Link>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-foreground-700 transition-colors whitespace-nowrap"
                >
                  <i className="ri-home-line"></i>
                  返回首页
                </Link>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-16">
            <div className="container-custom">
              <h2 className="font-heading text-xl text-foreground-900 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((a) => (
                  <Link
                    key={a.slug}
                    to={`/article/${a.slug}`}
                    className="block no-underline bg-background-50 rounded-lg border border-background-200/70 p-5 hover:border-primary-200 transition-all duration-300 group"
                  >
                    <h3 className="font-heading text-base text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {a.title}
                    </h3>
                    <p className="text-sm text-foreground-600 line-clamp-2 mb-3">
                      {a.excerpt}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-foreground-500 whitespace-nowrap">
                      <span>{a.publishedAt}</span>
                      <span>·</span>
                      <span>{a.readTime}阅读</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}